import getCssVar from 'src/utils/getCssVar'
import cssColorToHex from 'src/utils/cssColorToHex'

// v7's counterpart to src/utils/blackbaudAppearance (v6/Bootstrap). Same
// returned shape — these keys are passed straight into BlackbaudCheckout({...})
// and forwarded to the payment-details / mandate / cover iframes.
//
// Everything is read from the tokens the widget renders with, the way
// stripeAppearance.js does. Colors go through cssColorToHex because Blackbaud
// documents six-digit hex and our ramp resolves to oklch(); that also flattens
// a partner's three-digit brand shorthand, which --color-brand would otherwise
// pass through verbatim. An unreadable token comes back as '' and Blackbaud
// falls back to its own default, which is why nothing here carries a fallback
// of its own.
//
// Their styling surface is small and fixed: inputStyles takes borderRadius,
// labelStyle and fontFamily and nothing else — no padding, font-size, label
// color or focus state — so our fields and theirs can only converge by moving
// ours.
//
// Keep this object small. Blackbaud ships the entire config to its iframe as
// `config=btoa(JSON.stringify(...))` on the URL — see generateURL in their
// checkout script — so every byte here is a byte of query string, and going
// over blows the workflow up rather than degrading it. The font stack is what
// makes that tight: it costs ~280 characters per copy and there are slots that
// want three.
//
// Their docs say the nested fontFamily keys default to the "basic" font, i.e.
// the top-level one. They don't — omitting buttonTheme.selected.fontFamily
// rendered the method tab in Blackbaud's own face, not ours. Every slot that
// renders needs its own copy, so the stack itself has to be the thing that
// shrinks.
const BORDER_RADIUS = '8px' // --radius-lg (0.5rem); Blackbaud requires px

// Squeeze the stack for the wire without changing which face renders. Emoji
// families take part in glyph selection only for emoji codepoints, and nothing
// inside their iframe is one — card number, expiry, ZIP, the method tab labels
// — so they cost ~60 characters a copy and buy nothing. Re-joining without the
// space after each comma saves another 8; CSS doesn't need it.
//
// Deliberately narrow: only the copy riding on Blackbaud's query string is
// trimmed. Our own --font-sans keeps the full list, including the families
// Tailwind added to fix Japanese and Chinese rendering.
const compactFontStack = stack =>
  stack.split(',').map(family => family.trim()).filter(family => !/emoji/i.test(family)).join(',')

const blackbaudAppearance = () => {
  // A web font can't load inside Blackbaud's cross-origin iframe, and the
  // styling partial skips ours on Blackbaud fundraisers for that reason, so
  // this resolves to Tailwind's --font-sans — the stack our chrome is using.
  const fontFamily = compactFontStack(getCssVar('--default-font-family').trim())

  return {
    primaryColor: cssColorToHex(getCssVar('--color-brand')),
    // Blackbaud calls this "body text", but the only text in their iframe is
    // fields and field labels, so it tracks --input-text rather than
    // --body-text. Identical values today; separate tokens on purpose.
    fontColor: cssColorToHex(getCssVar('--input-text')),
    fontFamily,
    inputStyles: {
      borderRadius: BORDER_RADIUS,
      // v7's InputField always renders its label above the field.
      labelStyle: 'above',
      fontFamily,
    },
    buttonTheme: {
      borderRadius: BORDER_RADIUS,
      // v7 enables exactly one payment method per workflow (see
      // paymentMethodOptionsFor), so the ribbon collapses to a single
      // always-selected tab: a solid brand fill, which is our primary button
      // rather than our radio. Taking the text color from --btn-primary-text
      // is what makes this safe on a light brand — the styling partial flips
      // that token to gray-900 where white would fail contrast, and Blackbaud
      // only auto-picks a readable color when fontColor is omitted entirely.
      //
      // fontFamily is repeated here because it has to be — see the note above
      // on the "basic" font not inheriting. `unselected` is still omitted, but
      // on different grounds: no unselected tab ever renders in v7, so it's
      // dead config rather than a redundant copy.
      selected: {
        backgroundColor: cssColorToHex(getCssVar('--btn-primary-bg')),
        borderColor:     cssColorToHex(getCssVar('--btn-primary-border')),
        fontColor:       cssColorToHex(getCssVar('--btn-primary-text')),
        fontFamily,
      },
    },
  }
}

export default blackbaudAppearance
